import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  addMoney, Banner, Button, Card, compareMoney, Dialog, Field, formatMoney, isZero, Money, negate, PageHeader, Pill,
} from '@elysian/ui';
import type { Account, Contact, Journal, JournalSource } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { isAmount, iso, shortDate } from '../invoices/shared';
import { Attachments } from '../shared/Attachments';
import { Pager, ShowSize, SortTh, useClientList } from '../shared/ListControls';

const SOURCE: Record<JournalSource, string> = {
  manual: 'Manual journal', opening_balance: 'Opening balances', sales_invoice: 'Sales invoice', sales_credit: 'Credit note',
  purchase_bill: 'Bill', purchase_credit: 'Supplier credit', receipt: 'Money in', payment: 'Money out', bank_transfer: 'Transfer',
  reversal: 'Reversal', fx_revaluation: 'Currency revaluation', fx_realised: 'Exchange difference', year_end: 'Year end',
};

/** Where a journal that isn't reversed by hand is put right (#27); mirrors ledger.reverse_manual_journal. */
export function HowToCorrect({ journal: j }: { journal: Journal }) {
  const docPath = j.document_id && ({ sales_invoice: '/invoices/', sales_credit: '/credit-notes/', purchase_bill: '/bills/', purchase_credit: '/bills/' } as Record<string, string>)[j.document_type ?? ''];
  const doc = docPath ? <Link to={`${docPath}${j.document_id}`}>{j.document_no}</Link> : <>{j.document_no ?? 'the document'}</>;
  const text = (() => {
    switch (j.source) {
      case 'sales_invoice': return <>It came from invoice {doc}. To correct it, open the invoice and use <strong>Credit and reissue</strong>, or void it if nothing has been paid.</>;
      case 'sales_credit': return <>It came from credit note {doc}. To correct it, void the credit note.</>;
      case 'purchase_bill': return <>It came from bill {doc}. To correct it, void the bill or enter a supplier credit note.</>;
      case 'purchase_credit': return <>It came from supplier credit note {doc}. To correct it, void it.</>;
      case 'receipt': return <>It’s money received ({j.document_no ?? 'a receipt'}). Correct it on the <Link to="/bank">bank reconciliation</Link> (unmatch) or on the customer’s page.</>;
      case 'payment': return <>It’s money paid out ({j.document_no ?? 'a payment'}). Correct it on the <Link to="/bank">bank reconciliation</Link> (unmatch) or on the supplier’s page.</>;
      case 'bank_transfer': return <>It’s a transfer between bank accounts. Unmatch it on the <Link to="/bank">bank reconciliation</Link> to correct it.</>;
      case 'reversal': return <>It reverses {j.reverses_no ?? 'another journal'}. To undo it, post a new journal.</>;
      case 'opening_balance': return <>It’s an opening balance. Reverse its import batch on <Link to="/settings/import">Import data</Link> to correct it.</>;
      case 'fx_revaluation': return <>It’s a month-end revaluation; it reverses itself the next day.</>;
      case 'fx_realised': return <>It’s the exchange difference on a payment; correct the payment and it follows.</>;
      case 'year_end': return <>It closes a year. Reopen the year on <Link to="/year-end">Year end</Link> to correct it.</>;
      default: return null;
    }
  })();
  return text && <p className="el-hint" style={{ margin: '8px 0 0' }}><strong>Not reversed here.</strong> {text}</p>;
}

const debitOf = (a: string) => (compareMoney(a, '0') > 0 ? a : null);
const creditOf = (a: string) => (compareMoney(a, '0') < 0 ? negate(a) : null);

export function Journals() {
  const { source, can } = useSession();
  const toast = useToast((s) => s.show);
  const [params, setParams] = useSearchParams();
  const account = params.get('account') ?? '';
  const kind = (params.get('source') ?? '') as JournalSource | '';
  const [version, setVersion] = useState(0);
  const [open, setOpen] = useState<string | null>(params.get('open')); // a link from search opens that journal
  const [writing, setWriting] = useState(false);
  const [reversing, setReversing] = useState<Journal | null>(null);
  const data = useAsync(async () => {
    const [journals, accounts] = await Promise.all([
      source.journals({ account: account || undefined, source: kind || undefined }), source.accounts(undefined, true),
    ]);
    return { journals, accounts };
  }, [source, account, kind, version], { keep: true });
  const journals = data.state === 'ready' ? data.data.journals : [];
  const accounts = data.state === 'ready' ? data.data.accounts : [];
  // #49, #50: 50, 100 or all, and sorting on the headings, over every journal (newest first to start with).
  const amountOf = (j: Journal) => Number(addMoney('0', ...j.lines.map((l) => debitOf(l.amount) ?? '0')));
  const list = useClientList(journals, {
    key: 'elysian.journals', initial: { sort: 'date', dir: 'desc' }, filterKey: `${account}|${kind}`,
    sorts: { date: (j) => `${j.date} ${j.journal_no.padStart(20, '0')}`, number: (j) => j.journal_no, what: (j) => j.description.toLowerCase(),
      kind: (j) => SOURCE[j.source], amount: amountOf },
  });
  // A journal opened from a link (search, a bank transaction) is shown on whichever page it's on.
  const opened = useRef<string | null>(null);
  useEffect(() => {
    if (!open || opened.current === open || !list.size) return;
    const i = list.all.findIndex((x) => x.id === open);
    if (i >= 0) { opened.current = open; list.setPage(Math.floor(i / list.size)); }
  }, [open, list]);
  const show = (id: string | null | undefined) => {
    if (!id) return;
    if (!journals.some((x) => x.id === id)) setParams(new URLSearchParams());
    setOpen(id);
  };
  const mayReverse = can('journals.manual');
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  return (
    <>
      <PageHeader title="Journals" subtitle="Every entry in the books. Posted journals are never edited; a mistake is put right by reversing it."
        actions={<Button variant="primary" onClick={() => setWriting(true)}>New journal</Button>} />
      {data.state === 'error' && <Banner>Couldn’t load journals: {data.error.message}</Banner>}
      <Card>
        <div className="el-row" style={{ padding: '14px 16px' }}>
          <select className="el-input" aria-label="Account" style={{ width: 'auto', maxWidth: '100%' }} value={account} onChange={(e) => set('account', e.target.value)}>
            <option value="">All accounts</option>
            {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
          </select>
          <select className="el-input" aria-label="Kind" style={{ width: 'auto' }} value={kind} onChange={(e) => set('source', e.target.value)}>
            <option value="">Every kind</option>
            {(Object.keys(SOURCE) as JournalSource[]).map((k) => <option key={k} value={k}>{SOURCE[k]}</option>)}
          </select>
          <span style={{ marginLeft: 'auto' }}><ShowSize value={list.size} onChange={list.setSize} /></span>
        </div>
        <div className="el-table-wrap">
          <table className="el-table" aria-label="Journals" style={{ minWidth: 640 }}>
            <thead><tr>
              <SortTh label="Date" col="date" state={list.sortBy} onSort={list.setSortBy} firstDir="desc" />
              <SortTh label="Number" col="number" state={list.sortBy} onSort={list.setSortBy} />
              <SortTh label="What" col="what" state={list.sortBy} onSort={list.setSortBy} />
              <SortTh label="Kind" col="kind" state={list.sortBy} onSort={list.setSortBy} />
              <SortTh label="Amount" col="amount" state={list.sortBy} onSort={list.setSortBy} numeric />
              <th scope="col"><span className="el-vh">Actions</span></th>
            </tr></thead>
            <tbody>
              {data.state === 'loading' && <tr><td colSpan={6}><div className="el-skeleton" style={{ height: 18 }} /></td></tr>}
              {list.rows.map((j) => {
                const size = addMoney('0', ...j.lines.map((l) => debitOf(l.amount) ?? '0'));
                const expanded = open === j.id;
                return [
                  <tr key={j.id} data-href="#" onClick={() => setOpen(expanded ? null : j.id)}>
                    <td style={{ whiteSpace: 'nowrap' }}>{shortDate(j.date)}</td>
                    <td><button type="button" className="el-linkbtn" aria-expanded={expanded} onClick={(e) => { e.stopPropagation(); setOpen(expanded ? null : j.id); }}>{j.journal_no}</button></td>
                    <td>
                      {j.description}
                      {(j.reversed_by || j.reverses || (j.attachments?.length ?? 0) > 0) && (
                        <div className="el-hint">
                          {j.reversed_by && <>Reversed by <button type="button" className="el-linkbtn" onClick={(e) => { e.stopPropagation(); show(j.reversed_by); }}>{j.reversed_by_no ?? 'a later journal'}</button></>}
                          {j.reverses && <>Reverses <button type="button" className="el-linkbtn" onClick={(e) => { e.stopPropagation(); show(j.reverses); }}>{j.reverses_no ?? 'an earlier journal'}</button></>}
                          {(j.attachments?.length ?? 0) > 0 && <>{(j.reversed_by || j.reverses) && ' · '}{j.attachments!.length} {j.attachments!.length === 1 ? 'file' : 'files'}</>}
                        </div>
                      )}
                    </td>
                    <td><Pill tone={j.source === 'manual' ? 'info' : j.source === 'reversal' ? 'warn' : 'draft'}>{SOURCE[j.source]}</Pill></td>
                    <td className="el-num"><Money value={size} /></td>
                    <td className="el-num" style={{ width: 1, whiteSpace: 'nowrap' }}>
                      {j.source === 'manual' && !j.reversed_by && mayReverse && (
                        <Button size="sm" variant="ghost" aria-label={`Reverse ${j.journal_no}`} onClick={(e) => { e.stopPropagation(); setReversing(j); }}>Reverse</Button>
                      )}
                    </td>
                  </tr>,
                  expanded && (
                    <tr key={`${j.id}-lines`} className="el-journal-detail">
                      <td colSpan={6}>
                        <table className="el-table" aria-label={`Lines of ${j.journal_no}`}>
                          <thead><tr><th scope="col">Account</th><th scope="col">Note</th><th scope="col" className="el-num">Debit</th><th scope="col" className="el-num">Credit</th></tr></thead>
                          <tbody>
                            {j.lines.map((l) => (
                              <tr key={l.id}>
                                <td><span className="el-report-code">{l.account_code}</span>{l.account_name}</td>
                                <td className="el-hint">{l.description}</td>
                                <td className="el-num">{debitOf(l.amount) && <Money value={debitOf(l.amount)!} />}</td>
                                <td className="el-num">{creditOf(l.amount) && <Money value={creditOf(l.amount)!} />}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="el-spread" style={{ marginTop: 8 }}>
                          <span className="el-hint">Posted by {j.created_by.replace(/^ledger:user:|^demo:session$/, (m) => (m === 'demo:session' ? 'you' : ''))}</span>
                          {j.source === 'manual' && !j.reversed_by && mayReverse && <Button size="sm" variant="danger" onClick={() => setReversing(j)}>Reverse this journal</Button>}
                        </div>
                        {j.source !== 'manual' && <HowToCorrect journal={j} />}
                        <div style={{ marginTop: 12 }}>
                          <h2 className="el-label" style={{ margin: '0 0 6px' }}>Files</h2>
                          <Attachments files={j.attachments ?? []} target={{ journal_id: j.id }} canAdd={can('documents.write')} label={j.journal_no}
                            onChanged={() => setVersion((v) => v + 1)} />
                        </div>
                      </td>
                    </tr>
                  ),
                ];
              })}
              {data.state === 'ready' && journals.length === 0 && <tr><td colSpan={6} className="el-muted">No journals match.</td></tr>}
            </tbody>
          </table>
        </div>
        {data.state === 'ready' && <Pager count={list.count} page={list.page} size={list.size} shown={list.rows.length} onPage={list.setPage} />}
      </Card>
      {writing && data.state === 'ready' && (
        <NewJournal accounts={accounts.filter((a) => a.is_active)} onClose={() => setWriting(false)}
          onPosted={(j) => { setWriting(false); toast(`${j.journal_no} posted`); setOpen(j.id); setVersion((v) => v + 1); }} />
      )}
      {reversing && (
        <Reverse journal={reversing} onClose={() => setReversing(null)}
          onDone={(r) => { setReversing(null); toast(`${reversing.journal_no} reversed by ${r.journal_no}`); setOpen(r.id); setVersion((v) => v + 1); }} />
      )}
    </>
  );
}

interface Row { key: number; account: string; debit: string; credit: string; note: string; contact: string }
let rowKey = 0;
const blank = (): Row => ({ key: ++rowKey, account: '', debit: '', credit: '', note: '', contact: '' });

/** The amount a row carries, debit positive; null when unreadable or both sides filled. */
export function rowAmount(r: Pick<Row, 'debit' | 'credit'>): string | null {
  const d = r.debit.trim(), c = r.credit.trim();
  if (d && c) return null;
  if (d) return isAmount(d) ? addMoney(d) : null;
  if (c) return isAmount(c) ? negate(addMoney(c)) : null;
  return '0';
}

function NewJournal({ accounts, onClose, onPosted }: { accounts: Account[]; onClose: () => void; onPosted: (j: Journal) => void }) {
  const { source, today } = useSession();
  const contacts = useAsync(async () => [...await source.contacts('customer'), ...await source.contacts('supplier')], [source]);
  const [date, setDate] = useState(iso(today()));
  const [description, setDescription] = useState('');
  const [rows, setRows] = useState<Row[]>(() => [blank(), blank()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const amounts = rows.map(rowAmount);
  const totals = useMemo(() => ({
    debit: addMoney('0', ...amounts.map((a) => (a && compareMoney(a, '0') > 0 ? a : '0'))),
    credit: addMoney('0', ...amounts.map((a) => (a && compareMoney(a, '0') < 0 ? negate(a) : '0'))),
  }), [amounts]);
  const difference = addMoney(totals.debit, negate(totals.credit));
  const used = rows.filter((r, i) => r.account && amounts[i] && !isZero(amounts[i]!));
  const needsContact = (code: string) => ['debtors', 'creditors'].includes(accounts.find((a) => a.code === code)?.control ?? '');
  const problems = [
    ...(!description.trim() ? ['Describe what the journal is for.'] : []),
    ...(amounts.some((a) => a === null) ? ['A line has an amount that can’t be read, or both a debit and a credit.'] : []),
    ...(used.length < 2 ? ['Fill in at least two lines.'] : []),
    ...(rows.some((r) => needsContact(r.account) && !r.contact) ? ['Lines on debtors or creditors need a customer or supplier.'] : []),
    ...(!isZero(difference) ? [`Debits and credits differ by ${formatMoney(difference.replace('-', ''))}.`] : []),
  ];
  const setRow = (key: number, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  async function post() {
    setBusy(true);
    setError(null);
    try {
      onPosted(await source.createJournal({
        date, description: description.trim(),
        lines: used.map((r) => ({ account_code: r.account, amount: rowAmount(r)!, description: r.note.trim() || undefined, contact_id: r.contact || undefined })),
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Dialog title="New journal" onClose={onClose}
      actions={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || problems.length > 0} onClick={() => void post()}>Post journal</Button></>}>
      <div className="el-form-grid">
        <Field id="jn-date" label="Date"><input id="jn-date" type="date" className="el-input" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
      </div>
      <Field id="jn-desc" label="What it’s for"><input id="jn-desc" className="el-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Depreciation for the quarter" /></Field>
      <div className="el-stack" style={{ gap: 10 }}>
        {rows.map((r, i) => (
          <div key={r.key} className="el-journal-row">
            <select className="el-input" aria-label={`Line ${i + 1} account`} value={r.account} onChange={(e) => setRow(r.key, { account: e.target.value })}>
              <option value="">Account…</option>
              {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
            </select>
            <input className="el-input el-num" inputMode="decimal" aria-label={`Line ${i + 1} debit`} placeholder="Debit" value={r.debit} onChange={(e) => setRow(r.key, { debit: e.target.value })} />
            <input className="el-input el-num" inputMode="decimal" aria-label={`Line ${i + 1} credit`} placeholder="Credit" value={r.credit} onChange={(e) => setRow(r.key, { credit: e.target.value })} />
            {needsContact(r.account) && (
              <select className="el-input" aria-label={`Line ${i + 1} customer or supplier`} value={r.contact} onChange={(e) => setRow(r.key, { contact: e.target.value })}>
                <option value="">Customer or supplier…</option>
                {(contacts.state === 'ready' ? contacts.data : []).map((c: Contact) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
          </div>
        ))}
        <div><Button size="sm" onClick={() => setRows((rs) => [...rs, blank()])}>Add line</Button></div>
      </div>
      <dl className="el-totals" aria-label="Journal totals" style={{ marginLeft: 0 }}>
        <div><dt>Debits</dt><dd className="el-num"><Money value={totals.debit} /></dd></div>
        <div><dt>Credits</dt><dd className="el-num"><Money value={totals.credit} /></dd></div>
        <div style={{ color: isZero(difference) ? 'var(--el-brand)' : 'var(--el-warn-ink)' }}><dt>{isZero(difference) ? 'Balances' : 'Difference'}</dt><dd className="el-num"><Money value={difference.replace('-', '')} /></dd></div>
      </dl>
      {problems.length > 0 && used.length > 0 && <p className="el-hint" style={{ margin: 0 }}>{problems[0]}</p>}
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}

function Reverse({ journal, onClose, onDone }: { journal: Journal; onClose: () => void; onDone: (r: Journal) => void }) {
  const { source, today } = useSession();
  const [date, setDate] = useState(iso(today()));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function go() {
    setBusy(true);
    setError(null);
    try {
      onDone(await source.reverseJournal(journal.id, date, reason.trim()));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }
  return (
    <Dialog title={`Reverse ${journal.journal_no}?`} onClose={onClose}
      actions={<><Button variant="ghost" onClick={onClose}>Keep it</Button><Button variant="danger" disabled={busy || reason.trim().length < 3 || !date || date < journal.date} onClick={() => void go()}>Reverse</Button></>}>
      <p style={{ margin: 0 }}>A new journal is posted with every line the other way round, on the same accounts. Both stay in the books and link to each other, so the history is kept.</p>
      <p className="el-hint" style={{ margin: 0 }}>{journal.journal_no} · {shortDate(journal.date)} · {journal.description}</p>
      <Field id="rv-date" label="Date of the reversal" hint="Usually the date of the journal itself, or today. Not before the journal.">
        <input id="rv-date" type="date" className="el-input" min={journal.date} value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
      <Field id="rv-reason" label="Reason"><input id="rv-reason" className="el-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Posted to the wrong account" /></Field>
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}
